<template>
  <div class="rounded-md bg-white p-6 shadow-md">
    <div class="space-y-4" ref="results">
      <h2 class="mb-2 text-center text-xl font-semibold text-gray-700">
        Lista de comprobantes de tasas varias
      </h2>
      <div
        class="flex flex-col-reverse items-center space-y-4 space-y-reverse lg:flex-row lg:justify-between lg:space-y-0"
      >
        <button
          v-if="!isLoading"
          @click="showFilters = true"
          class="focus:shadow-outline inline-flex justify-center space-x-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm transition duration-150 ease-in-out hover:bg-cool-gray-200 focus:border-blue-300 focus:outline-none sm:text-sm"
        >
          <span class="text-center text-sm font-bold">Filtros</span>
          <SolidFilterIcon class="h-5 w-5" />
        </button>
        <button
          v-if="!isLoading"
          @click="fetchComprobantes"
          class="focus:shadow-outline inline-flex justify-center space-x-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm transition duration-150 ease-in-out hover:bg-cool-gray-200 focus:border-blue-300 focus:outline-none sm:text-sm"
        >
          <span class="text-center text-sm font-bold">Actualizar</span>
          <OutlineRefreshIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="flex flex-wrap gap-4">
        <div
          v-for="(filtro, index) in selectedFilters"
          :key="index"
          class="flex flex-col justify-center rounded-xl bg-slate-200 px-3 py-0.5 text-center text-sm shadow-sm"
        >
          <span class="font-medium">{{ filtro.key }}</span>
          <span>{{ filtro.value }}</span>
        </div>
      </div>

      <transition
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <Cargando v-if="isLoading" key="1" />
        <Empty key="2" v-else-if="comprobantesTerceros.length === 0" />
        <div key="3" v-else class="space-y-3">
          <ListaComprobantesTerceros
            :comprobantesTerceros="comprobantesTerceros"
            @refresh="fetchComprobantes"
          />
        </div>
      </transition>
    </div>
    <!-- Modal con filtros -->
    <FiltrosComprobantesTerceros
      :showFilters="showFilters"
      @filters:change="onFilterChange"
      @filters:hide="showFilters = false"
    />
  </div>
</template>

<script>
import moment from 'moment'

import { useService } from '@colescba/app-vue'
import { useSession, useAcreditacionServicio } from '@colescba/ui'

import { useAccessControl } from '@/composables'

import ListaComprobantesTerceros from '@/components/ListaComprobantesTerceros'
import FiltrosComprobantesTerceros from '@/components/FiltrosComprobantesTerceros'
import Cargando from '@/components/Core/Cargando'
import Empty from '@/components/Core/Empty'

export default {
  name: 'Comprobantes',

  mixins: [useService('portalPagos'), useSession(), useAcreditacionServicio(), useAccessControl()],

  components: { ListaComprobantesTerceros, FiltrosComprobantesTerceros, Cargando, Empty },

  data() {
    return {
      isLoading: true,
      comprobantesTerceros: [],
      filtros: null,
      showFilters: false,
    }
  },

  computed: {
    selectedFilters() {
      let filters = []
      if (this.filtros?.estadoPago)
        filters.push({ key: 'Estado pago', value: this.filtros.estadoPago.descripcion })
      if (this.filtros?.estadoUso)
        filters.push({ key: 'Estado uso', value: this.filtros.estadoUso.descripcion })
      if (this.filtros?.fechaCreacion.desde || this.filtros?.fechaCreacion.hasta) {
        let fechaDesde = this.filtros?.fechaCreacion.desde
          ? moment(this.filtros?.fechaCreacion.desde).format('DD-MM-YYYY')
          : null
        let fechaHasta = this.filtros?.fechaCreacion.hasta
          ? moment(this.filtros?.fechaCreacion.hasta).format('DD-MM-YYYY')
          : null
        let separador = fechaDesde && fechaHasta ? ' - ' : ' '
        filters.push({
          key: 'Fecha de creación',
          value: `${fechaDesde || 'Hasta'}${separador}${fechaHasta || 'en adelante'}`,
        })
      }
      if (this.filtros?.fechaVencimiento.desde || this.filtros?.fechaVencimiento.hasta) {
        let fechaDesde = this.filtros?.fechaVencimiento.desde
          ? moment(this.filtros?.fechaVencimiento.desde).format('DD-MM-YYYY')
          : null
        let fechaHasta = this.filtros?.fechaVencimiento.hasta
          ? moment(this.filtros?.fechaVencimiento.hasta).format('DD-MM-YYYY')
          : null
        let separador = fechaDesde && fechaHasta ? ' - ' : ' '
        filters.push({
          key: 'Fecha de vencimiento',
          value: `${fechaDesde || 'Hasta'}${separador}${fechaHasta || 'en adelante'}`,
        })
      }
      if (this.filtros?.sede)
        filters.push({ key: 'Delegación', value: this.filtros.sede.descripcion })
      return filters
    },
  },

  methods: {
    async fetchComprobantes() {
      this.isLoading = true
      let filtros = {
        pago_id: this.filtros?.estadoPago.id.trim() || 'A_VERIFICAR',
        uso_id: this.filtros?.estadoUso.id.trim() || 'USADO',
      }
      if (this.filtros?.fechaCreacion.desde) {
        filtros['fecha_desde'] = moment(this.filtros?.fechaCreacion.desde).format('DD-MM-YYYY')
      }
      if (this.filtros?.fechaCreacion.hasta) {
        filtros['fecha_hasta'] = moment(this.filtros?.fechaCreacion.hasta).format('DD-MM-YYYY')
      }
      if (this.filtros?.fechaVencimiento.desde) {
        filtros['fecha_ven_desde'] = moment(this.filtros?.fechaVencimiento.desde).format(
          'DD-MM-YYYY'
        )
      }
      if (this.filtros?.fechaVencimiento.hasta) {
        filtros['fecha_ven_hasta'] = moment(this.filtros?.fechaVencimiento.hasta).format(
          'DD-MM-YYYY'
        )
      }
      if (this.filtros?.sede) {
        filtros['sede_id'] = this.filtros.sede.id.trim()
      }
      this.comprobantesTerceros = await this.portalPagos.findComprobantesPorUsoYPago(
        this.user.cuitCuil.numero,
        filtros
      )
      this.isLoading = false
    },
    onFilterChange(filtros) {
      this.showFilters = false
      this.filtros = filtros
      this.fetchComprobantes()
    },
  },
}
</script>
